import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button/button";
import { Input } from "@/components/ui/input/input";
import { Label } from "@/components/ui/label/label";
import { loginTeamLeader } from "@/config/mutation/team-leader";
import { useAuthContext } from "@/lib/use-auth";

export const TeamLeaderLoginForm = () => {
  const navigate = useNavigate();
  const { setUser, setisAuth } = useAuthContext();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const sessionEnded = params.get("ended") === "1";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const { mutate, isPending } = useMutation({
    mutationFn: loginTeamLeader,
    onSuccess: (data) => {
      if (!data?.key) return toast.error("Login failed");
      // Nothing cached from an earlier (possibly ended) session may carry over.
      queryClient.removeQueries({ queryKey: ["teamleader"] });
      setUser(data.key, "teamleader");
      setisAuth(true);
      toast.success("Login Successful!");
      setTimeout(() => navigate("/teamleader"), 200);
    },
    onError: (error) => {
      const msg =
        error instanceof AxiosError
          ? (error.response?.data?.error as string) || error.message
          : (error as Error).message;
      toast.error(msg);
    },
  });

  return (
    <form
      className="max-w-md w-full flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (!email || !password) return toast.error("Enter both email and password");
        mutate({ email, password });
      }}
    >
      {sessionEnded && (
        <p className="text-sm text-amber-300" data-testid="tl-session-ended">
          Your session has ended. Please log in again.
        </p>
      )}
      <div className="flex flex-col gap-2">
        <Label htmlFor="tl-email">Email</Label>
        <Input
          id="tl-email"
          name="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="border-white/50"
          disabled={isPending}
        />
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="tl-password">Password</Label>
        <Input
          id="tl-password"
          name="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="border-white/50"
          disabled={isPending}
        />
      </div>
      <Button
        type="submit"
        disabled={isPending}
        className="gap-2 bg-[#f4ca8d] text-[#01020d] hover:bg-[#f4ca8d]/80"
      >
        {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
        Log in
      </Button>
    </form>
  );
};
